import { router } from "@inertiajs/react"
import { type ChangeEvent, useId, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { BrandCoverBand } from "@/components/brand/brand-cover-band"
import {
  CoverCropper,
  type CoverCropperLabels,
} from "@/components/cover-cropper"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  BRAND_COVER_ASPECT_CLASS,
  BRAND_COVER_ASPECT_RATIO,
} from "@/lib/brand-cover"
import { cn } from "@/lib/utils"
import { brandSettingsProfileCoverImagePath } from "@/routes"

/**
 * OKS-304 / OKS-855 — Brand cover image upload + remove widget for the
 * Brand Settings → Profile page.
 *
 * Sits between the existing `<BrandLogoUploader />` and the brand-name
 * `<Input>` per UX Spec §2.1. Mirrors the `<BrandLogoUploader />`
 * affordance: hidden `<input type="file" name="brand[cover_image]">`
 * inside the parent Inertia `<Form>` (multipart auto-detected by
 * Inertia when a file input is present in the form).
 *
 * OKS-855 — every pick/replace of an accepted image now routes through
 * the shared `CoverCropper` (a 3:2 `react-easy-crop` step) BEFORE anything
 * is staged, mirroring `MasterclassCoverImageField` (OKS-768). The
 * OS-picked original is cleared from the input on pick; only the cropped
 * 3:2 Blob is injected back (via `DataTransfer`) and submitted, so no
 * uncropped image ever reaches the server and the framed area is exactly
 * what every surface renders (client-crop-authoritative). A non-image pick
 * bypasses the cropper and defers to server content-type validation.
 *
 * Affordances (UX Spec §2.1):
 *   - Replace button → opens the hidden file picker; an accepted image
 *     opens the 3:2 cropper, a non-image stays on the input for server
 *     validation.
 *   - Remove button (gated on `currentUrl` truthy) → opens a
 *     destructive `<Dialog>` with Cancel as primary-focus per
 *     OKS destructive-pattern precedent (`RevokeDialog` in
 *     `Invitations/Index.tsx`); confirm fires
 *     `router.delete(brandSettingsProfileCoverImagePath())`. Unchanged by
 *     OKS-855 — the brand cover keeps its dedicated DELETE route rather
 *     than a staged remove-flag.
 *   - Read-only mode (Brand Member tier): hides Replace + Remove,
 *     leaves the preview band visible.
 *
 * Preview precedence (UX Spec §2): freshly cropped file (object URL) →
 * current resolved cover (`currentUrl`) → the band's dark fallback.
 *
 * Crew form rules: plain shadcn `Input`/`Label`/`Button`/`Dialog`; NO
 * shadcn Form / react-hook-form / zod. The crop widget is NOT a form.
 * Errors render from the parent `<Form>` render-prop `errors.cover_image`.
 *
 * Brand isolation: this is a presentation-only widget; the
 * `currentUrl` + `brandName` props come from server-rendered Inertia
 * props which are derived from `Current.brand` only.
 */

/**
 * OKS-855 — Accepted cover MIME types (mirrors the server's
 * `Brand::COVER_IMAGE_CONTENT_TYPES`). Drives BOTH the file input's
 * `accept` filter AND the crop-vs-bypass decision on pick: a non-image
 * type can't be framed, so it skips the cropper and defers to server
 * validation (OKS-697 contract).
 */
export const COVER_ACCEPT_TYPES = ["image/jpeg", "image/png", "image/webp"]

const BRAND_COVER_CROP_LABELS: CoverCropperLabels = {
  title: "settings.brand.profile.cover_crop_title",
  description: "settings.brand.profile.cover_crop_description",
  zoom: "settings.brand.profile.cover_crop_zoom",
  cancel: "settings.brand.profile.cover_crop_cancel",
  apply: "settings.brand.profile.cover_crop_apply",
  decodeError: "settings.brand.profile.cover_crop_decode_error",
}

export interface BrandCoverImageUploaderProps {
  /** Current cover URL from Inertia props (`brand.cover_image_url`)
   *  — null when no cover attached. */
  currentUrl: string | null
  /** Brand display name — passed through to `<BrandCoverBand />` so
   *  the dark fallback monogram has the right initial. */
  brandName: string
  /** Wire-format strong-params input name. Default
   *  `brand[cover_image]` matches `BrandSettingsProfilesController#profile_params`. */
  inputName?: string
  /** Disable affordances for Brand Member / Agency Member read-only tiers. */
  readOnly?: boolean
  /** Test-id wrapper. */
  testId?: string
}

export function BrandCoverImageUploader({
  currentUrl,
  brandName,
  inputName = "brand[cover_image]",
  readOnly = false,
  testId = "brand-cover-image-uploader",
}: BrandCoverImageUploaderProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const inputId = useId()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [removeOpen, setRemoveOpen] = useState(false)
  const [removeProcessing, setRemoveProcessing] = useState(false)
  // OKS-855 — crop step. A picked/replaced accepted image opens the cropper;
  // only the cropped (3:2) Blob is ever staged into the hidden multipart
  // input, so the uncropped original is never submitted. `cropSrc` is the
  // object URL handed to `react-easy-crop`; `cropMime` passes the source type
  // through to the canvas export.
  const [cropOpen, setCropOpen] = useState(false)
  const [cropSrc, setCropSrc] = useState<string | null>(null)
  const [cropMime, setCropMime] = useState<string | undefined>(undefined)
  // OKS-855 — edit-load skeleton (UX Spec §3). Gates ONLY the server-provided
  // `currentUrl` branch: a freshly picked/cropped local preview is an
  // in-memory object URL with no meaningful network wait, so it's never
  // skeleton-gated. Resets whenever `currentUrl` itself changes (React's
  // documented "resetting state when a prop changes" pattern) so a stale
  // "loaded" flag can't hide a fresh skeleton opportunity. The skeleton
  // settles when the server cover resolves EITHER way — `onImageLoad` (paint)
  // OR `onImageError` (a 404 from an expired/purged signed URL, which never
  // fires onLoad): settling on error is what lets the band's graceful dark
  // fallback show instead of a stuck skeleton + hidden band (OKS-304 contract).
  const [currentUrlLoaded, setCurrentUrlLoaded] = useState(false)
  const [trackedCurrentUrl, setTrackedCurrentUrl] = useState(currentUrl)
  if (currentUrl !== trackedCurrentUrl) {
    setTrackedCurrentUrl(currentUrl)
    setCurrentUrlLoaded(false)
  }

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      setPreviewUrl(null)
      return
    }

    // OKS-855 — a NON-image type can't be framed by the cropper. Bypass the
    // crop step and leave the raw file on the input so the server's
    // content-type validation produces the canonical inline error
    // (`errors.cover_image`) on Save. Only accepted image types route
    // through the mandatory crop.
    if (!COVER_ACCEPT_TYPES.includes(file.type)) {
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return URL.createObjectURL(file)
      })
      return
    }

    // Image: route every pick/replace through the crop step. The OS picker
    // populates the hidden input with the UNCROPPED original; clear it
    // immediately so a form submit before *Apply* can't send the raw file.
    // Only `onCropApply` re-populates the input (with the 3:2 Blob).
    if (inputRef.current) inputRef.current.value = ""
    setCropMime(file.type || undefined)
    setCropSrc(URL.createObjectURL(file))
    setCropOpen(true)
  }

  // OKS-855 — apply the cropped 3:2 Blob: wrap it in a File, inject it into
  // the hidden multipart input via `DataTransfer` (the only way to
  // programmatically set `input.files`), and update the preview.
  const onCropApply = (blob: Blob) => {
    const ext =
      blob.type === "image/png"
        ? "png"
        : blob.type === "image/webp"
          ? "webp"
          : "jpg"
    const file = new File([blob], `brand-cover.${ext}`, {
      type: blob.type || "image/jpeg",
    })
    if (inputRef.current) {
      const transfer = new DataTransfer()
      transfer.items.add(file)
      inputRef.current.files = transfer.files
    }
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return URL.createObjectURL(blob)
    })
    closeCropper()
  }

  const onCropCancel = () => {
    // No file staged — the input stays cleared, the prior preview/cover is
    // untouched (UX Spec §1 "Cancel crop").
    closeCropper()
  }

  const closeCropper = () => {
    setCropOpen(false)
    if (cropSrc) URL.revokeObjectURL(cropSrc)
    setCropSrc(null)
    setCropMime(undefined)
  }

  const onClear = () => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })
    if (inputRef.current) inputRef.current.value = ""
  }

  // Replace button delegates to the hidden file input. Same idiom as
  // every other "trigger a hidden input" widget on shadcn.
  const onReplaceClick = () => {
    inputRef.current?.click()
  }

  const handleRemove = () => {
    setRemoveProcessing(true)
    router.delete(brandSettingsProfileCoverImagePath(), {
      preserveScroll: true,
      onFinish: () => {
        setRemoveProcessing(false)
        setRemoveOpen(false)
        // Also clear any client-side preview so the post-delete page
        // reload starts from a clean state.
        onClear()
      },
    })
  }

  // Preview shows: the picked-but-not-yet-saved file > the persisted
  // cover URL > nothing (band falls back to dark monogram).
  const displayUrl = previewUrl ?? currentUrl
  // OKS-855 — true only while `displayUrl` IS the server-provided
  // `currentUrl` (no local pick in front of it) — the only branch the
  // edit-load skeleton applies to.
  const showingServerCover = !previewUrl && Boolean(currentUrl)

  return (
    <div data-testid={testId} className="space-y-3">
      <Label htmlFor={inputId}>{t("settings.brand.profile.cover_label")}</Label>

      <div className="w-full max-w-2xl">
        {/* OKS-855 — edit-load skeleton: shown only while the server-provided
            `currentUrl` is loading for the first time; the band stays mounted
            (so the cover `<img>`'s `onLoad` still fires) but visually hidden
            underneath until it resolves. */}
        {showingServerCover && !currentUrlLoaded ? (
          <Skeleton
            data-testid={`${testId}-loading-skeleton`}
            className={cn(BRAND_COVER_ASPECT_CLASS, "w-full rounded-lg")}
          />
        ) : null}
        <BrandCoverBand
          coverUrl={displayUrl}
          brandName={brandName}
          onImageLoad={() => {
            if (showingServerCover) setCurrentUrlLoaded(true)
          }}
          onImageError={() => {
            // A 404 on the server cover settles the skeleton the OTHER way so
            // the dark fallback band shows (never a stuck loader).
            if (showingServerCover) setCurrentUrlLoaded(true)
          }}
          className={cn(
            "w-full rounded-lg",
            showingServerCover && !currentUrlLoaded && "hidden",
          )}
          testId={`${testId}-preview`}
        />
      </div>

      <p className="text-muted-foreground max-w-2xl text-xs">
        {t("settings.brand.profile.cover_helper")}
      </p>

      {/* Hidden file input is ALWAYS in the DOM (read-only or not) so the
          parent Inertia `<Form>` auto-detects multipart. In read-only
          mode the affordance buttons are hidden and the user can't reach
          the input — but its presence is harmless because the form-level
          Save button is also hidden in that mode (parent Profile.tsx). */}
      {readOnly ? null : (
        <Input
          ref={inputRef}
          id={inputId}
          type="file"
          name={inputName}
          accept={COVER_ACCEPT_TYPES.join(",")}
          onChange={onPick}
          className="sr-only"
          data-testid={`${testId}-input`}
          aria-label={t("settings.brand.profile.cover_label")}
        />
      )}

      {readOnly ? null : (
        <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onReplaceClick}
            data-testid={`${testId}-replace`}
            className="min-h-11"
          >
            {t("settings.brand.profile.cover_replace")}
          </Button>

          {previewUrl ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClear}
              data-testid={`${testId}-clear`}
              className="text-muted-foreground sm:self-center"
            >
              {t("settings.brand.profile.cover_clear")}
            </Button>
          ) : null}

          {currentUrl ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setRemoveOpen(true)}
              data-testid={`${testId}-remove`}
              className="min-h-11 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              {t("settings.brand.profile.cover_remove")}
            </Button>
          ) : null}
        </div>
      )}

      <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <DialogContent data-testid={`${testId}-remove-dialog`}>
          <DialogTitle>
            {t("settings.brand.profile.cover_remove_confirm_title")}
          </DialogTitle>
          <DialogDescription>
            {t("settings.brand.profile.cover_remove_confirm_body")}
          </DialogDescription>
          <DialogFooter>
            {/* Cancel is primary-focus per UX Spec §6 — destructive
                  action is NEVER the default focus target of a Dialog
                  (mirrors `RevokeDialog` in Invitations/Index.tsx). */}
            <DialogClose
              render={
                <Button
                  variant="secondary"
                  autoFocus
                  disabled={removeProcessing}
                  data-testid={`${testId}-remove-cancel`}
                >
                  {t("settings.brand.profile.cover_remove_confirm_cancel")}
                </Button>
              }
            />
            <Button
              variant="destructive"
              onClick={handleRemove}
              disabled={removeProcessing}
              data-testid={`${testId}-remove-confirm`}
            >
              {t("settings.brand.profile.cover_remove_confirm_cta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OKS-855 — crop-to-3:2 step for every accepted image pick/replace. */}
      <CoverCropper
        open={cropOpen}
        imageSrc={cropSrc}
        mimeType={cropMime}
        aspectRatio={BRAND_COVER_ASPECT_RATIO}
        aspectClass={BRAND_COVER_ASPECT_CLASS}
        labels={BRAND_COVER_CROP_LABELS}
        onApply={onCropApply}
        onCancel={onCropCancel}
        testId="brand-cover-crop"
      />
    </div>
  )
}

export default BrandCoverImageUploader
